import type { ReactNode } from "react";
import { Loader2, StickyNote } from "lucide-react";
import { cn } from "@/lib/utils";
import { Skeleton } from "./ui/skeleton";

export function InitialLoading() {
  return (
    <main
      aria-busy="true"
      aria-live="polite"
      aria-label="Loading Slip"
      className="grid min-h-screen grid-rows-[auto_1fr_auto] overflow-x-hidden bg-white text-neutral-950"
    >
      <LoadingPanel className="flex min-h-20 min-w-0 flex-col gap-4 rounded-none border-0 px-4 py-4 shadow-none lg:flex-row lg:items-center lg:justify-between lg:px-6">
        <div className="flex min-w-0 flex-wrap items-center gap-3">
          <div className="flex min-w-0 items-center gap-2 text-2xl font-semibold tracking-normal text-neutral-950">
            <span className="grid size-9 place-items-center rounded-md border border-neutral-200 bg-white text-neutral-950 shadow-[0_1px_1px_rgba(0,0,0,0.04)]">
              <StickyNote size={22} strokeWidth={2.4} />
            </span>
            <span>Slip</span>
          </div>
          <span className="inline-flex min-h-8 items-center gap-2 rounded-full bg-neutral-100 px-3 text-sm font-medium text-neutral-700 ring-1 ring-neutral-200">
            <Loader2 className="animate-spin" size={14} />
            Loading
          </span>
          <Skeleton className="h-5 w-20 rounded-full" />
        </div>

        <div className="grid min-w-0 grid-cols-4 gap-2 sm:flex sm:flex-wrap sm:justify-end">
          <Skeleton className="h-10 rounded-lg sm:w-28" />
          <Skeleton className="h-10 rounded-lg sm:w-28" />
          <Skeleton className="h-10 rounded-lg sm:w-24" />
          <Skeleton className="h-10 rounded-lg sm:w-10" />
        </div>
      </LoadingPanel>

      <section className="grid min-h-0 min-w-0 grid-cols-1 gap-4 border-y border-neutral-200 bg-neutral-50/60 p-4 lg:grid-cols-[minmax(0,1fr)_minmax(360px,0.42fr)]">
        <LoadingPanel className="grid min-h-[520px] min-w-0 grid-rows-[minmax(0,1fr)_auto] lg:min-h-0">
          <div className="flex flex-col gap-4 p-5 sm:p-7">
            <Skeleton className="h-5 w-4/5 max-w-2xl" />
            <Skeleton className="h-5 w-11/12 max-w-3xl" />
            <Skeleton className="h-5 w-3/5 max-w-xl" />
            <div className="h-4" />
            <Skeleton className="h-5 w-10/12 max-w-3xl" />
            <Skeleton className="h-5 w-7/12 max-w-2xl" />
            <Skeleton className="h-5 w-9/12 max-w-2xl" />
          </div>
          <footer className="flex min-h-14 flex-wrap items-center gap-3 border-t border-neutral-200 px-4 py-3">
            <Skeleton className="h-4 w-24" />
            <Skeleton className="h-4 w-16" />
            <Skeleton className="h-4 w-14" />
            <span className="flex-1" />
            <Skeleton className="h-9 w-28 rounded-lg" />
          </footer>
        </LoadingPanel>

        <LoadingPanel className="min-h-[520px] min-w-0 lg:min-h-0">
          <div className="flex h-14 items-end gap-5 border-b border-neutral-200 px-4 pb-3">
            <Skeleton className="h-5 w-14" />
            <Skeleton className="h-5 w-20" />
            <Skeleton className="h-5 w-16" />
          </div>
          <div className="border-b border-neutral-200 p-4">
            <Skeleton className="h-24 rounded-lg" />
          </div>
          <div className="flex flex-col gap-3 p-4">
            <FileRowSkeleton />
            <FileRowSkeleton />
            <FileRowSkeleton />
          </div>
        </LoadingPanel>
      </section>

      <LoadingPanel className="flex min-h-14 min-w-0 flex-col gap-2 rounded-none border-0 px-4 py-3 shadow-none sm:flex-row sm:items-center lg:px-6">
        <Skeleton className="h-5 w-full max-w-md" />
        <span className="flex-1" />
        <Skeleton className="h-5 w-28" />
      </LoadingPanel>
    </main>
  );
}

function LoadingPanel({ className, children }: { className?: string; children: ReactNode }) {
  return (
    <section className={cn("overflow-hidden rounded-lg border border-neutral-200 bg-white shadow-[0_1px_2px_rgba(0,0,0,0.04),0_8px_24px_rgba(0,0,0,0.03)]", className)}>
      {children}
    </section>
  );
}

function FileRowSkeleton() {
  return (
    <div className="grid min-h-[72px] grid-cols-[52px_minmax(0,1fr)_auto] items-center gap-3 rounded-lg border border-neutral-200 bg-white p-2 shadow-[0_1px_1px_rgba(0,0,0,0.03)]">
      <Skeleton className="h-13 w-13 rounded-md" />
      <div className="flex min-w-0 flex-col gap-2">
        <Skeleton className="h-4 w-4/5" />
        <Skeleton className="h-3 w-2/5" />
      </div>
      <div className="flex items-center gap-1">
        <Skeleton className="size-10 rounded-md" />
        <Skeleton className="size-10 rounded-md" />
      </div>
    </div>
  );
}
